import {
	FieldMappingSourceType,
	SALESFORCE_OPPORTUNITY_RESERVED_FIELD_KEYS,
	SalesforceEntityType,
	SalesforceField,
	SalesforceFieldMapping,
	SalesforceSourceField,
	SalesforceSourceFieldsResponse,
} from "../schema/salesforce";
import { NewSalesforceMapping } from "./SalesforceMappingRow";

/** One tab of the mapping modal — one sObject each. */
export enum SalesforceMappingTab {
	CONTACTS = "CONTACTS",
	ACCOUNTS = "ACCOUNTS",
	LEADS = "LEADS",
	OPPORTUNITIES = "OPPORTUNITIES",
}

export const SALESFORCE_TAB_ENTITY: Record<SalesforceMappingTab, SalesforceEntityType> = {
	[SalesforceMappingTab.CONTACTS]: SalesforceEntityType.CONTACT,
	[SalesforceMappingTab.ACCOUNTS]: SalesforceEntityType.ACCOUNT,
	[SalesforceMappingTab.LEADS]: SalesforceEntityType.LEAD,
	[SalesforceMappingTab.OPPORTUNITIES]: SalesforceEntityType.OPPORTUNITY,
};

/**
 * The fields a tab may target. Opportunity mappings hide the fields the deal config and the
 * sync's relationship linking own — `POST field-mappings` rejects them with a 400.
 */
export const targetFieldsForTab = (
	tab: SalesforceMappingTab,
	fields: SalesforceField[]
): SalesforceField[] =>
	tab === SalesforceMappingTab.OPPORTUNITIES
		? fields.filter((field) => !SALESFORCE_OPPORTUNITY_RESERVED_FIELD_KEYS.includes(field.name))
		: fields;

/**
 * Whether a field is hidden because the deal config owns it, as opposed to because Salesforce
 * will not accept a write.
 *
 * The distinction matters in the UI: a reserved field is perfectly writable and the customer
 * *can* set it — under Opportunity Configuration. Labelling it read-only would send them
 * looking for a permission problem that does not exist.
 */
export const isDealConfigReservedField = (tab: SalesforceMappingTab, fieldName: string): boolean =>
	tab === SalesforceMappingTab.OPPORTUNITIES &&
	SALESFORCE_OPPORTUNITY_RESERVED_FIELD_KEYS.includes(fieldName);

/**
 * The reserved fields the org actually has, so the Opportunity tab can name them instead of
 * silently omitting them from the picker. Filtering by what the describe returned keeps the note
 * honest — an org without `RecordTypeId` on its Opportunity layout should not be told about it.
 */
export const dealConfigReservedFieldsFor = (
	tab: SalesforceMappingTab,
	fields: SalesforceField[]
): SalesforceField[] => fields.filter((field) => isDealConfigReservedField(tab, field.name));

/**
 * Whether Salesforce itself refuses the write — a formula field or one the connected user's
 * profile cannot update. Unlike a reserved field, nothing the customer does in Signalist
 * changes this.
 */
export const isUnwritableField = (field: SalesforceField): boolean =>
	Boolean(field.isCalculated || field.isReadOnly);

/**
 * The Signalist-side fields a tab may read from. `source-fields` is keyed per sObject, so each
 * tab has a direct key; `contactFields` is the fallback for the two the backend marks optional.
 */
export const sourceFieldsForTab = (
	tab: SalesforceMappingTab,
	sourceFields: SalesforceSourceFieldsResponse | undefined
): SalesforceSourceField[] => {
	switch (tab) {
		case SalesforceMappingTab.ACCOUNTS:
			return sourceFields?.accountFields ?? [];
		case SalesforceMappingTab.LEADS:
			return sourceFields?.leadFields ?? sourceFields?.contactFields ?? [];
		case SalesforceMappingTab.OPPORTUNITIES:
			return sourceFields?.opportunityFields ?? sourceFields?.contactFields ?? [];
		default:
			return sourceFields?.contactFields ?? [];
	}
};

const isFieldMode = (sourceType?: FieldMappingSourceType): boolean =>
	(sourceType ?? FieldMappingSourceType.FIELD) === FieldMappingSourceType.FIELD;

/** Source paths already mapped on this tab, so the picker cannot offer one twice. */
export const collectUsedSourceFields = (
	saved: SalesforceFieldMapping[] | undefined,
	unsaved: NewSalesforceMapping[]
): string[] => [
	...(saved ?? [])
		.filter((mapping) => isFieldMode(mapping.sourceType) && mapping.sourceField)
		.map((mapping) => mapping.sourceField),
	...unsaved
		.filter((mapping) => isFieldMode(mapping.sourceType) && mapping.sourceField)
		.map((mapping) => mapping.sourceField),
];

/**
 * Target fields already mapped on this tab, per source type — the same field may be fed by a
 * FIELD and by an AI mapping, so the exclusion is per bucket rather than global.
 */
export const collectUsedTargetFields = (
	saved: SalesforceFieldMapping[] | undefined,
	unsaved: NewSalesforceMapping[]
): Record<FieldMappingSourceType, string[]> => {
	const byType: Record<FieldMappingSourceType, string[]> = {
		[FieldMappingSourceType.FIELD]: [],
		[FieldMappingSourceType.CONSTANT]: [],
		[FieldMappingSourceType.AI]: [],
	};

	(saved ?? [])
		.filter((mapping) => mapping.salesforceField)
		.forEach((mapping) =>
			byType[mapping.sourceType ?? FieldMappingSourceType.FIELD].push(mapping.salesforceField)
		);

	unsaved
		.filter((mapping) => mapping.salesforceField)
		.forEach((mapping) => byType[mapping.sourceType].push(mapping.salesforceField));

	return byType;
};

/**
 * The allowed values an AI mapping's fallback is checked against.
 *
 * Only a **restricted** picklist constrains the write: an unrestricted one accepts a new value
 * and simply stores it, so treating its options as a closed set would reject a perfectly legal
 * fallback. `optionsAreRestricted` is the flag that separates the two.
 */
export const restrictedValuesOf = (field: SalesforceField | undefined): string[] =>
	field?.optionsAreRestricted ? (field.options ?? []).map((option) => option.value) : [];
